import { scrollPageTo, scrollPageBy } from '../pageScroll';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import FoodSelectionModal from './FoodSelectionModal';
import UnifiedAddModal from './UnifiedAddModal';
import {
  MAX_MEAL_FOOD_BATCH,
  accountScopeOf,
  claimFoodBatchOperation,
  createFoodBatchOperation,
  dropFoodBatchClaim,
  foodBatchError,
  isRejectedFoodBatchError,
  isSameAccountScope,
  isSettledFoodBatchOperation,
  markFoodBatchOperationUnknown,
  readFoodBatchOperation,
  settleFoodBatchOperation,
} from './mealFoodBatchOperations';
import WaterPickerModal from './WaterPickerModal';
import RecipeScaleModal from './RecipeScaleModal';
import { TrashIcon, PlusIcon, LightBulbIcon, BeakerIcon, ScaleIcon, Bars3Icon, CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import WaterGlassPlusIcon from './WaterGlassPlusIcon';
import FoodStackPlusIcon from './FoodStackPlusIcon';
import CameraPlusIcon from './CameraPlusIcon';
import PhotoPlusIcon from './PhotoPlusIcon';
import './Meal.css';
import '../animations/MealAnimations.css';
import { isActivityOngoing } from './Activity';
import { calculateFoodNutrients } from '../../utils/Nutrition';
import { useMoveFoodsOptional } from './MoveFoodsContext';
import FloatingSelectionActions from '../FloatingSelectionActions';
import { mealMoveModeVisibility } from './mealMoveModeVisibility';
import GenerationCue from './GenerationCue';
import MealSuggestionFailure from './MealSuggestionFailure';
import useMealSuggestionJob from './useMealSuggestionJob';
import { mealSuggestionError } from '../../services/mealSuggestionJobs';
import MealSuggestionRequestForm from './MealSuggestionRequestForm';
import { useMealSuggestionDraft } from './useMealSuggestionDraft';
import { suppressNumericInputContextMenu } from '../numericInputContextMenu';
import RecipeStepTimer from './RecipeStepTimer';
import { extractTimedWait } from './recipeStepTimers';
import { IDLE_COMPLETION, SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton } from './SubmitFeedback';

const ItemType = {
  MEAL_ITEM: 'mealItem',
};

/**
 * Custom Drag Layer - renders the dragged item following the cursor
 */
const MealCustomDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) {
    return null;
  }

  const transform = `translate(${currentOffset.x}px, ${currentOffset.y}px)`;

  return (
    <div className="custom-drag-layer">
      <div className="drag-preview" style={{ transform }}>
        <div className="reorder-drag-handle">
          <Bars3Icon style={{ width: 20, height: 20 }} />
        </div>
        <div className="reorder-exercise-content">
          {item.isRecipe && <BeakerIcon style={{ width: 16, height: 16, color: '#a78bfa' }} />}
          <span className="reorder-exercise-name">{item.name || 'Item'}</span>
          <span className="reorder-exercise-sets">{item.detail}</span>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_ROW_HEIGHT = 52;

/**
 * FLIP: snapshot row positions, apply `mutate`, then slide each row from its
 * old position to its new one. Rows are measured after the mutation commits,
 * so an in-flight slide is re-based rather than restarted from a stale origin.
 */
const runFlipAnimation = (mutate) => {
  const prevRects = new Map();
  document.querySelectorAll('.reorder-compact-item:not(.dragging)').forEach(el => {
    prevRects.set(el, el.getBoundingClientRect());
  });

  mutate();

  requestAnimationFrame(() => {
    prevRects.forEach((prevRect, el) => {
      if (!el.isConnected) return;
      const deltaY = prevRect.top - el.getBoundingClientRect().top;
      if (Math.abs(deltaY) < 1) return;

      // Clear the transition before the offset so the previous slide cannot
      // animate toward the new starting point.
      el.style.transition = 'none';
      el.style.transform = `translateY(${deltaY}px)`;
      requestAnimationFrame(() => {
        el.style.transition = 'transform 0.2s ease-out';
        el.style.transform = 'translateY(0)';
      });
    });
  });
};

const commitPendingDrop = (draggedItem, moveItem) => {
  const pendingDrop = draggedItem.pendingDrop;
  if (!pendingDrop) return;

  moveItem(
    draggedItem.sourceMealId,
    draggedItem.index,
    pendingDrop.mealId,
    pendingDrop.index
  );
  draggedItem.sourceMealId = pendingDrop.mealId;
  draggedItem.index = pendingDrop.index;
  delete draggedItem.pendingDrop;
};

/**
 * Cross-meal hover preview. Opens a gap the size of the dragged row so the
 * target meal reads exactly like a same-meal reorder, where the dragged row
 * is hidden in place and leaves an empty slot behind.
 */
const previewInsert = (moveFoods, draggedItem, mealId, index) => {
  draggedItem.pendingDrop = { mealId, index };
  if (moveFoods.isDropPreviewAt(mealId, index)) return;
  runFlipAnimation(() => moveFoods.setDropPreview(mealId, index, draggedItem.height));
};

/**
 * The empty slot shown in the destination meal while a cross-meal drag is in
 * flight. Also accepts the drop so releasing over the gap commits the move.
 */
const MealDropPlaceholder = ({ mealId, height, moveItem }) => {
  const [, drop] = useDrop({
    accept: ItemType.MEAL_ITEM,
    canDrop: (draggedItem) => draggedItem.sourceMealId !== mealId,
    drop(draggedItem) {
      commitPendingDrop(draggedItem, moveItem);
    },
  });

  return <div ref={drop} className="meal-drop-placeholder" style={{ height }} aria-hidden="true" />;
};

/**
 * Tail drop zone rendered after the last item in a meal during move mode.
 * Lets users drop a food into an empty meal or at the very end of a
 * non-empty list. Only reacts to items dragged from a different meal —
 * within-meal "drop at end" is already covered by the last item's hover
 * handler.
 */
const MealTailDropZone = ({ mealId, itemCount, moveItem }) => {
  const moveFoods = useMoveFoodsOptional();
  const [, drop] = useDrop({
    accept: ItemType.MEAL_ITEM,
    canDrop: (draggedItem) => draggedItem.sourceMealId !== mealId,
    hover(draggedItem) {
      if (draggedItem.sourceMealId === mealId) {
        delete draggedItem.pendingDrop;
        return;
      }
      previewInsert(moveFoods, draggedItem, mealId, itemCount);
    },
    drop(draggedItem) {
      commitPendingDrop(draggedItem, moveItem);
    },
  });
  return (
    <div
      ref={drop}
      className={`meal-tail-drop-zone ${itemCount === 0 ? 'is-empty' : ''}`}
    >
      {itemCount === 0 && <span className="meal-tail-drop-hint">Drop foods here</span>}
    </div>
  );
};

const MealInsertDropZone = ({ mealId, index, moveItem }) => {
  const moveFoods = useMoveFoodsOptional();
  const [, drop] = useDrop({
    accept: ItemType.MEAL_ITEM,
    canDrop: (draggedItem) => draggedItem.sourceMealId !== mealId,
    hover(draggedItem) {
      if (draggedItem.sourceMealId === mealId) return;
      previewInsert(moveFoods, draggedItem, mealId, index);
    },
    drop(draggedItem) {
      commitPendingDrop(draggedItem, moveItem);
    },
  });

  return <div ref={drop} className="meal-insert-drop-zone" aria-hidden="true" />;
};

/**
 * Draggable compact meal item for move-foods mode. Supports drag-drop
 * within the same meal AND across sibling meals on the same day. The
 * drag item carries `sourceMealId` and `originalMealId` so the hover
 * handler and the drop-end persister know which meals were touched.
 */
const DraggableCompactMealItem = ({
  item,
  index,
  mealId,
  moveItem,
  onDragEnd,
  isRecipe = false,
  name,
  detail
}) => {
  const ref = useRef(null);
  const moveFoods = useMoveFoodsOptional();

  const [, drop] = useDrop({
    accept: ItemType.MEAL_ITEM,
    canDrop: (draggedItem) => draggedItem.sourceMealId === mealId,
    hover(draggedItem, monitor) {
      if (!ref.current) return;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const sameMeal = draggedItem.sourceMealId === mealId;
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      const hoverRect = ref.current.getBoundingClientRect();
      const isPastMiddle = clientOffset.y - hoverRect.top > hoverRect.height / 2;

      if (!sameMeal) {
        // Keep the active drag source in its original meal until drop. Moving
        // it during hover would re-parent the React-DnD source and can cancel
        // the drag before the user releases over the sibling meal.
        previewInsert(moveFoods, draggedItem, mealId, isPastMiddle ? hoverIndex + 1 : hoverIndex);
        return;
      }

      delete draggedItem.pendingDrop;
      if (dragIndex === hoverIndex) return;

      // Only swap once the pointer passes the midpoint of the hovered row.
      // Swapping on first contact reorders the list under a still cursor,
      // which puts a new row under it and swaps straight back.
      if (dragIndex < hoverIndex && !isPastMiddle) return;
      if (dragIndex > hoverIndex && isPastMiddle) return;

      moveFoods.clearDropPreview();
      runFlipAnimation(() => moveItem(draggedItem.sourceMealId, dragIndex, mealId, hoverIndex));
      // Update the drag item so subsequent hovers know its new position.
      draggedItem.index = hoverIndex;
    },
    drop(draggedItem) {
      commitPendingDrop(draggedItem, moveItem);
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType.MEAL_ITEM,
    item: () => ({
      type: ItemType.MEAL_ITEM,
      id: item.recipe_group_id || item.food?.instance_id || item.food?._client_id || item.food?.food_id,
      index,
      sourceMealId: mealId,
      originalMealId: mealId,
      height: ref.current?.getBoundingClientRect().height || DEFAULT_ROW_HEIGHT,
      name,
      detail,
      isRecipe
    }),
    end: (draggedItem) => {
      moveFoods.clearDropPreview();
      // Persist on drop only. We pass the set of meals that were touched
      // (origin and destination) so cross-meal moves write both meals in
      // a single backend call.
      if (onDragEnd) {
        const touched = new Set([draggedItem.originalMealId, draggedItem.sourceMealId].filter(Boolean));
        onDragEnd(Array.from(touched));
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Use empty image as default drag preview (we render custom one)
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  useEffect(() => {
    if (!isDragging) return undefined;

    document.body.classList.add('meal-item-drag-active');
    return () => document.body.classList.remove('meal-item-drag-active');
  }, [isDragging]);

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`reorder-compact-item ${isDragging ? 'dragging' : ''} ${isRecipe ? 'is-recipe' : ''}`}
    >
      <div className="reorder-drag-handle">
        <Bars3Icon style={{ width: 20, height: 20 }} />
      </div>
      <div className="reorder-item-summary">
        {isRecipe && <BeakerIcon className="icon-sm" style={{ color: '#a78bfa' }} />}
        <span className="reorder-item-name">{name}</span>
        <span className="reorder-item-detail">{detail}</span>
      </div>
    </div>
  );
};

// Internal component for handling quantity input with local state to prevent jumping/locking
const MealQuantityInput = ({ value, onCommit, scopeKey, className, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const editingRef = useRef(false);
  const valueRef = useRef(value);
  const scopeKeyRef = useRef(scopeKey);
  const commitRef = useRef({ latest: 0, settled: 0 });
  valueRef.current = value;
  scopeKeyRef.current = scopeKey;

  useEffect(() => {
    const commits = commitRef.current;
    if (!isEditing && commits.settled >= commits.latest) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  useEffect(() => {
    editingRef.current = false;
    commitRef.current = { latest: 0, settled: 0 };
    setIsEditing(false);
    setLocalValue(value);
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    
    // Attempt commit if it looks valid
    const parsed = Number(newVal);
    if (newVal.trim() !== '' && Number.isFinite(parsed) && parsed >= 0) {
      const commitScope = scopeKey;
      const sequence = ++commitRef.current.latest;
      Promise.resolve(onCommit(parsed)).then(() => {
        if (scopeKeyRef.current !== commitScope || sequence !== commitRef.current.latest) return;
        commitRef.current.settled = sequence;
        if (!editingRef.current) setLocalValue(newVal);
      }).catch(() => {
        if (scopeKeyRef.current !== commitScope || sequence !== commitRef.current.latest) return;
        commitRef.current.settled = sequence;
        if (!editingRef.current) setLocalValue(valueRef.current);
      });
    }
  };

  const handleBlur = () => {
    editingRef.current = false;
    setIsEditing(false);
    const commits = commitRef.current;
    if (commits.settled >= commits.latest) setLocalValue(valueRef.current);
  };
  
  const handleFocus = (e) => {
      editingRef.current = true;
      setIsEditing(true);
      e.target.select();
  };

  return (
    <input
      {...props}
      type="number"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onContextMenu={suppressNumericInputContextMenu}
      className={className}
    />
  );
};

const UNIT_OPTIONS = ['serving', 'g', 'ml', 'oz', 'lb', 'cup', 'tbsp', 'tsp'];

const CONVERSION_RATES = {
  g: 1,
  ml: 1, // Will apply density factor in logic
  oz: 28.35,
  lb: 453.6,
  cup: 240, // ml, will apply density
  tbsp: 15, // ml, will apply density
  tsp: 5,   // ml, will apply density
  serving: 1
};

// Helper to calculate number of servings from a quantity and unit
const calculateServings = (quantity, unit, source) => {
  if (!source || !quantity) return null;
  
  const servingWeight = parseFloat(source.serving_weight) || null;
  const servingVolume = parseFloat(source.serving_volume) || null;
  const density = parseFloat(source.density) || 1;
  
  // Fallback calculation if no serving_weight/volume
  let gramsPerServing = servingWeight;
  let mlPerServing = servingVolume;
  
  if (!servingWeight && !servingVolume) {
    const sSize = parseFloat(source.serving_size) || 100;
    const sUnit = source.serving_quantity_unit || source.serving_unit || 'g';
    if (['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(sUnit)) {
      mlPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
      gramsPerServing = mlPerServing * density;
    } else {
      gramsPerServing = sSize * (CONVERSION_RATES[sUnit] || 1);
      mlPerServing = density > 0 ? gramsPerServing / density : gramsPerServing;
    }
  } else {
    gramsPerServing = servingWeight || (servingVolume * density);
    mlPerServing = servingVolume || (servingWeight / density);
  }
  
  const isVolumeUnit = (u) => ['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(u);
  const isMassUnit = (u) => ['g', 'oz', 'lb'].includes(u);
  
  const qty = parseFloat(quantity) || 0;
  
  if (unit === 'serving') {
    return qty;
  } else if (isVolumeUnit(unit)) {
    const mlValue = qty * (CONVERSION_RATES[unit] || 1);
    return mlPerServing > 0 ? mlValue / mlPerServing : qty;
  } else if (isMassUnit(unit)) {
    const gramsValue = qty * (CONVERSION_RATES[unit] || 1);
    return gramsPerServing > 0 ? gramsValue / gramsPerServing : qty;
  }
  
  return null;
};

const createMealOperationId = () => (typeof window !== 'undefined' && window.crypto?.randomUUID?.())
  || `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;

const Meal = ({ meal, finishActivity, backendService, modalService, onActivityRefresh, readOnly = false, completion = IDLE_COMPLETION }) => {
  // Ensure every food has a stable client-side id we can use as a React key during
  // reorder. Legacy foods may not have a server-assigned `instance_id`; without a
  // stable identity, reordering changes their position-derived key, which causes
  // React to unmount/remount the row mid-drag and silently kills drag-and-drop.
  const ensureClientIds = (arr) => (arr || []).map(f =>
    (f && (f.instance_id || f._client_id))
      ? f
      : { ...f, _client_id: `cid-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}` }
  );

  const [foods, setFoods] = useState(() => ensureClientIds(meal.data.foods));
  const [pendingMutationCount, setPendingMutationCount] = useState(0);
  const foodRevisionRef = useRef({ mealId: String(meal._id), value: Number(meal.food_revision) || 0 });
  if (foodRevisionRef.current.mealId !== String(meal._id)) {
    foodRevisionRef.current = { mealId: String(meal._id), value: Number(meal.food_revision) || 0 };
  }
  const mutationScopeRef = useRef(null);
  const currentMutationScope = mutationScopeRef.current;
  if (!currentMutationScope
    || currentMutationScope.mealId !== String(meal._id)
    || currentMutationScope.backendService !== backendService
    || currentMutationScope.readOnly !== readOnly
    || !isSameAccountScope(backendService, currentMutationScope.account)) {
    if (currentMutationScope) currentMutationScope.active = false;
    mutationScopeRef.current = {
      mealId: String(meal._id),
      backendService,
      // The signed-in principal can change in place on the same service.
      account: accountScopeOf(backendService),
      readOnly,
      active: true,
      pending: 0,
      tail: Promise.resolve(),
    };
  }
  const runMealMutation = (mutation, { refresh = true, scope: expectedScope = null } = {}) => {
    const scope = mutationScopeRef.current;
    // Callers bound to an earlier meal/service scope never dispatch into this one.
    if (expectedScope && expectedScope !== scope) return Promise.resolve(null);
    const isLive = () => scope.active && mutationScopeRef.current === scope
      && isSameAccountScope(scope.backendService, scope.account);
    scope.pending += 1;
    if (mutationScopeRef.current === scope) setPendingMutationCount(scope.pending);
    const task = scope.tail.catch(() => {}).then(async () => {
      if (!isLive()) return null;
      const updatedMeal = await mutation();
      if (!isLive()) return null;
      const revision = Number(updatedMeal.food_revision) || 0;
      if (revision < foodRevisionRef.current.value) return null;
      foodRevisionRef.current.value = revision;
      setFoods(ensureClientIds(updatedMeal.data?.foods || updatedMeal.foods || []));
      if (refresh) {
        try {
          Promise.resolve(onActivityRefresh?.(updatedMeal)).catch(error => {
            console.error('Meal saved; activity refresh failed', error);
          });
        } catch (error) {
          console.error('Meal saved; activity refresh failed', error);
        }
      }
      return updatedMeal;
    });
    scope.tail = task.catch(() => {});
    return task.finally(() => {
      scope.pending -= 1;
      if (mutationScopeRef.current === scope) setPendingMutationCount(scope.pending);
    });
  };
  useEffect(() => {
    const scope = mutationScopeRef.current;
    scope.active = true;
    setPendingMutationCount(scope.pending);
    return () => {
      scope.active = false;
      // A render may have replaced the scope (e.g. account change) without this effect re-running.
      mutationScopeRef.current.active = false;
    };
  }, [meal._id, backendService, readOnly]);
  const [legacyFoodMap, setLegacyFoodMap] = useState({});
  const [isSuggesting, setIsSuggesting] = useState(false);
  const suggestionScope = JSON.stringify([meal._id, meal.status, meal.name, meal.data.foods, meal.data.nutrition_target, readOnly]);
  const { start: startSuggestionJob, cancel: cancelSuggestionJob, isCurrent: isCurrentSuggestionJob } = useMealSuggestionJob(suggestionScope);
  const [suggestion, setSuggestion] = useState(meal.data.suggestion || null);
  const lastSuggestionRequestRef = useRef({ constraints: null, targetCalories: null, images: [] });

  // Move-foods mode is shared across all meals on the same day via the
  // MoveFoodsContext provided by Activities. When the user toggles it on
  // any meal, every sibling meal in the same day group enters move mode
  // simultaneously and foods can be dragged within or between them.
  const moveFoods = useMoveFoodsOptional();
  const mealIdStr = String(meal._id);
  const moveActive = moveFoods.isActive;
  const moveVisibility = mealMoveModeVisibility({ type: 'meal', moveActive });

  // Self-register this meal with the day-scoped move-foods context so
  // that entering move mode from any sibling pulls our current foods into
  // the shared working state.
  const { registerMeal, unregisterMeal } = moveFoods;
  useEffect(() => {
    const persistedFoods = foods.map(({ _client_id, ...food }) => food);
    registerMeal({
      ...meal,
      data: { ...meal.data, foods: persistedFoods },
      _mealMutationPending: pendingMutationCount > 0,
    });
  }, [meal, foods, pendingMutationCount, registerMeal]);
  useEffect(() => () => unregisterMeal(mealIdStr), [mealIdStr, unregisterMeal]);

  // Sync foods when the meal prop is updated externally (e.g. via WebSocket activity_updated).
  // Skipped while move mode is active so an in-flight WS update from our own persist
  // doesn't clobber the user's optimistic drag state.
  const mealFoodsRef = useRef(meal.data.foods);
  useEffect(() => {
    if (moveActive) {
      // Stay in sync with what we currently show; defer prop-driven updates until exit.
      return;
    }
    const revision = Number(meal.food_revision) || 0;
    // WebSocket/feed reads can finish after a newer acknowledged mutation.
    // Food revisions advance atomically with every server-side food write.
    if (revision < foodRevisionRef.current.value) return;
    const newerRevision = revision > foodRevisionRef.current.value;
    foodRevisionRef.current.value = revision;
    if (newerRevision || meal.data.foods !== mealFoodsRef.current) {
      mealFoodsRef.current = meal.data.foods;
      setFoods(ensureClientIds(meal.data.foods));
    }
  }, [meal._id, meal.food_revision, meal.data.foods, moveActive]);

  // While move mode is active, mirror the shared context's working state for
  // this meal into local `foods`. This way, when the user clicks "Done" and
  // the context state is cleared, the local state already reflects the moves
  // — the view doesn't snap back to the pre-move snapshot while we wait for
  // the WS-triggered prop refresh.
  const ctxFoods = moveActive ? moveFoods.getFoodsFor(mealIdStr) : null;
  useEffect(() => {
    if (!moveActive) return;
    if (!Array.isArray(ctxFoods)) return;
    if (ctxFoods === foods) return;
    setFoods(ensureClientIds(ctxFoods));
  }, [ctxFoods, moveActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showConstraintPrompt, setShowConstraintPrompt] = useState(false);
  const {
    constraintInput,
    setConstraintInput,
    targetCaloriesInput,
    setTargetCaloriesInput,
    includeExisting,
    setIncludeExisting,
    photoEntries,
    photoFeedback,
    addPhotos,
    removePhoto,
  } = useMealSuggestionDraft(meal._id);
  const [createRecipeMode, setCreateRecipeMode] = useState(false);
  const [selectedForRecipe, setSelectedForRecipe] = useState(new Set());
  useEffect(() => {
    if (moveActive || (moveFoods.recipeOwnerMealId !== undefined && moveFoods.recipeOwnerMealId !== mealIdStr)) {
      // Ownership changes invalidate UI completion, not the in-flight write.
      // Keep its pending gate until settlement so re-entry cannot double-submit.
      if (createRecipeMode) {
        recipeConversionRef.current.generation += 1;
        recipeConversionRef.current.intent = null;
      }
      setCreateRecipeMode(false);
      setSelectedForRecipe(new Set());
    }
  }, [moveActive, moveFoods.recipeOwnerMealId, mealIdStr, createRecipeMode]);
  const [isCreatingRecipe, setIsCreatingRecipe] = useState(false);
  const recipeConversionRef = useRef({ pending: false, intent: null, generation: 0 });
  const exitRecipeRef = useRef(moveFoods.exitRecipe);
  exitRecipeRef.current = moveFoods.exitRecipe;
  useEffect(() => {
    // Invalidate both local UI and its shared day lock. The owner-conditional
    // release cannot clear a newer sibling selection; the ref avoids resetting
    // on unrelated provider-value changes (or standalone fallback functions).
    exitRecipeRef.current(mealIdStr);
    recipeConversionRef.current = { pending: false, intent: null, generation: recipeConversionRef.current.generation + 1 };
    setIsCreatingRecipe(false);
    setCreateRecipeMode(false);
    setSelectedForRecipe(new Set());
    const state = recipeConversionRef.current;
    return () => { state.generation += 1; state.disposed = true; };
  }, [mealIdStr, backendService, readOnly]);
  const [isAddingIngredient, setIsAddingIngredient] = useState(null); // idx of ingredient being added

  // Strip parenthesized qualifiers and common preparation/descriptor words
  // from recipe ingredient names so the food DB search isn't over-specific.
  // e.g. "fresh shredded chicken breast (boneless)" -> "chicken breast"
  const INGREDIENT_QUALIFIERS = new Set([
    'fresh', 'frozen', 'dried', 'dry', 'canned', 'jarred', 'packaged',
    'raw', 'cooked', 'uncooked', 'baked', 'roasted', 'boiled', 'steamed',
    'grilled', 'fried', 'pan-fried', 'sauteed', 'sautéed', 'broiled',
    'shredded', 'grated', 'chopped', 'diced', 'minced', 'sliced', 'cubed',
    'crushed', 'ground', 'whole', 'halved', 'quartered', 'peeled', 'unpeeled',
    'seeded', 'pitted', 'rinsed', 'drained', 'softened', 'melted', 'cold',
    'warm', 'hot', 'room-temperature', 'ripe', 'unripe', 'overripe',
    'organic', 'natural', 'homemade', 'store-bought', 'pure', 'plain',
    'unsweetened', 'sweetened', 'unsalted', 'salted', 'low-sodium',
    'low-fat', 'reduced-fat', 'fat-free', 'nonfat', 'lean', 'extra-lean',
    'lite', 'light', 'skim', 'whole-milk', 'low-carb', 'sugar-free',
    'small', 'medium', 'large', 'extra-large', 'jumbo', 'mini',
    'finely', 'coarsely', 'thinly', 'thickly', 'roughly', 'lightly',
    'optional', 'to-taste', 'divided', 'plus-more', 'or-more',
    'boneless', 'skinless', 'bone-in', 'skin-on',
  ]);
  const cleanIngredientName = (name) => {
    if (!name) return '';
    // 1. Drop parenthesized text and bracketed asides.
    let cleaned = name.replace(/\s*\([^)]*\)/g, '').replace(/\s*\[[^\]]*\]/g, '');
    // 2. Drop everything after the first comma (typically prep notes).
    cleaned = cleaned.split(',')[0];
    // 3. Tokenize, drop qualifiers, keep the rest.
    const tokens = cleaned
      .toLowerCase()
      .split(/\s+/)
      .map(t => t.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
      .filter(t => t && !INGREDIENT_QUALIFIERS.has(t));
    return tokens.join(' ').trim() || cleaned.trim();
  };

  const parseIngredientQuantity = (ingredient) => {
    const quantityText = String(ingredient.quantity || '').trim().toLowerCase();
    const unicodeFractions = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };
    const mixedFraction = quantityText.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)/);
    const fraction = quantityText.match(/^(\d+)\s*\/\s*(\d+)/);
    const decimal = quantityText.match(/^(\d+(?:\.\d+)?)/);
    const unicodeFraction = quantityText.match(/^(\d+)?\s*([¼½¾⅓⅔⅛⅜⅝⅞])/);

    let quantity = 1;
    if (mixedFraction && Number(mixedFraction[3]) !== 0) {
      quantity = Number(mixedFraction[1]) + Number(mixedFraction[2]) / Number(mixedFraction[3]);
    } else if (fraction && Number(fraction[2]) !== 0) {
      quantity = Number(fraction[1]) / Number(fraction[2]);
    } else if (unicodeFraction) {
      quantity = Number(unicodeFraction[1] || 0) + unicodeFractions[unicodeFraction[2]];
    } else if (decimal) {
      quantity = Number(decimal[1]);
    }

    const unitText = [quantityText, ingredient.unit, ingredient.serving_unit, ingredient.default_unit]
      .filter(Boolean)
      .join(' ');
    const unitPrefix = '(?:^|[\\d\\s.,/()])';
    const unitPatterns = [
      ['fl oz', new RegExp(`${unitPrefix}(?:fl\\.?\\s*oz|fluid ounces?)\\b`)],
      ['tbsp', new RegExp(`${unitPrefix}(?:tbsp|tablespoons?)\\b`)],
      ['tsp', new RegExp(`${unitPrefix}(?:tsp|teaspoons?)\\b`)],
      ['cup', new RegExp(`${unitPrefix}cups?\\b`)],
      ['ml', new RegExp(`${unitPrefix}(?:ml|millilit(?:er|re)s?)\\b`)],
      ['g', new RegExp(`${unitPrefix}(?:g|grams?)\\b`)],
      ['oz', new RegExp(`${unitPrefix}(?:oz|ounces?)\\b`)],
      ['lb', new RegExp(`${unitPrefix}(?:lb|lbs|pounds?)\\b`)],
      ['serving', new RegExp(`${unitPrefix}servings?\\b`)]
    ];
    const matchedUnit = unitPatterns.find(([, pattern]) => pattern.test(unitText));

    return { quantity, unit: matchedUnit ? matchedUnit[0] : 'g' };
  };

  const handleAddFoodFromSuggestion = async (ingredient, idx) => {
    setIsAddingIngredient(idx);
    try {
      // 1. Search for matching food in user's DB
      const searchResults = await backendService.getFoods(cleanIngredientName(ingredient.name), 0, 20);
      
      const { quantity: qtyValue, unit: qtyUnit } = parseIngredientQuantity(ingredient);

      const suggestedFoodData = {
        name: ingredient.name,
        calories: ingredient.calories,
        protein: ingredient.protein,
        carbs: ingredient.carbs,
        fat: ingredient.fat,
        serving_size: qtyValue,
        serving_unit: qtyUnit,
        source: 'user'
      };

      // Show options to user: Use existing, Create new, or Cancel
      modalService.open(FoodSelectionModal, {
        backendService,
        initialQuantity: qtyValue,
        initialUnit: qtyUnit,
        initialFood: suggestedFoodData,
        manageMode: false,
        // Passing searchResults gives it context for "preexisting" check logic
        resultsOverride: searchResults, 
        suggestionContext: { ingredient, searchTerm: cleanIngredientName(ingredient.name), idx },
        hideCloseButton: true,
        onSelect: async (foodId, quantity, unit) => {
          try {
            await runMealMutation(() => backendService.addFoodToMeal(meal._id, foodId, quantity, unit));
          } catch (e) {
            alert("Failed to add food: " + e.message);
          }
        }
      });

    } catch (e) {
      console.error("Failed to initiate food add from suggestion", e);
      alert("Failed to start adding item: " + e.message);
    } finally {
      setIsAddingIngredient(null);
    }
  };

  const handleAddAllFromSuggestion = async () => {
    if (!suggestion || !suggestion.ingredients) return;
    
    const ingredientsToProcess = [...suggestion.ingredients];
    let stopped = false;
    
    // Recursive function to process ingredients one by one
    const processNext = async (index) => {
      if (stopped || index >= ingredientsToProcess.length) {
        // Finished all items - don't dismiss, just finish
        return;
      }

      const ingredient = ingredientsToProcess[index];
      
      try {
        const searchResults = await backendService.getFoods(cleanIngredientName(ingredient.name), 0, 20);
        
        const { quantity: qtyValue, unit: qtyUnit } = parseIngredientQuantity(ingredient);

        const suggestedFoodData = {
          name: ingredient.name,
          calories: ingredient.calories,
          protein: ingredient.protein,
          carbs: ingredient.carbs,
          fat: ingredient.fat,
          serving_size: qtyValue,
          serving_unit: qtyUnit,
          source: 'user'
        };

        modalService.open(FoodSelectionModal, {
          backendService,
          initialQuantity: qtyValue,
          initialUnit: qtyUnit,
          initialFood: suggestedFoodData,
          manageMode: false,
          resultsOverride: searchResults, 
          suggestionContext: {
            ingredient,
            searchTerm: cleanIngredientName(ingredient.name),
            idx: index,
            isBatch: true,
            total: ingredientsToProcess.length
          },
          hideCloseButton: true,
          onSelect: async (foodId, quantity, unit) => {
            try {
              await runMealMutation(() => backendService.addFoodToMeal(meal._id, foodId, quantity, unit));
              
              // Proceed to next ingredient
              processNext(index + 1);
            } catch (e) {
              alert("Failed to add food: " + e.message);
              // Still try next item
              processNext(index + 1);
            }
          },
          onSkip: () => {
            // User skipped this one, move to next
            processNext(index + 1);
          },
          onStop: () => {
            stopped = true;
          }
        });
      } catch (e) {
        console.error("Batch add item failed", e);
        processNext(index + 1);
      }
    };

    processNext(0);
  };

  useEffect(() => {
    // Determine which foods need looking up:
    // 1. "Legacy" foods (missing name/stats in snapshot)
    // 2. Local foods (to ensure we display latest edited version)
    // We skip 'off_' foods if they have a snapshot, assuming they don't change often.
    const foodsToFetch = foods.filter(f => 
       f.food_id && (
         !f.name || // Legacy/broken snapshot
         !String(f.food_id).startsWith('off_') // Local food: Always fetch latest
       )
    );

    if (foodsToFetch.length > 0) {
        const fetchMissing = async () => {
             // Start with existing map (empty on fresh mount)
            const newMap = { ...legacyFoodMap };
            let hasUpdates = false;

            await Promise.all(foodsToFetch.map(async (f) => {
                if (newMap[f.food_id]) return; // already have it in current state
                try {
                    const details = await backendService.getFoodById(f.food_id);
                    if (details) {
                        newMap[f.food_id] = details;
                        hasUpdates = true;
                    }
                } catch (e) {
                    console.error("Failed to fetch details for " + f.food_id);
                }
            }));
            
            if (hasUpdates) {
                 setLegacyFoodMap(newMap);
            }
        };
        fetchMissing();
    }
  }, [foods, backendService, legacyFoodMap]);

  const handleAddFood = async (foodId, quantity, unit, virtualFoodData) => {
    try {
      let resolvedFoodId = foodId;

      // Handle virtual foods created from multi-ingredient photo detection
      if (foodId === 'create_virtual' && virtualFoodData) {
        const created = await backendService.createFood(virtualFoodData);
        resolvedFoodId = created._id;
        quantity = virtualFoodData.serving_size || 1;
        unit = virtualFoodData.serving_unit || 'serving';
      }

      await runMealMutation(() => backendService.addFoodToMeal(meal._id, resolvedFoodId, quantity, unit));
    } catch (error) {
      console.error('Error adding food to meal:', error);
      if (error.response && error.response.status === 409) {
          alert('This food is already in your meal.');
      } else {
          alert('Failed to add food to meal.');
      }
    }
  };

  // Picker batches are owned by account + meal (see mealFoodBatchOperations),
  // not by this component or the picker: retries, reopened pickers and
  // remounts reuse the unconfirmed operation key, and a different selection
  // first settles that key on the server so it can never append later.
  // `restored` is the operation a reopened picker was restored from; once it
  // has settled, the picker's submit is reconciled with that outcome.
  const handleAddFoods = async (items, scope = mutationScopeRef.current, restored = null) => {
    const service = scope.backendService;
    const { identity } = scope.account;
    const isLive = () => scope === mutationScopeRef.current && scope.active
      && isSameAccountScope(service, scope.account);
    if (!isLive()) return { status: 'stale', dispatched: false };
    const foods = items.map(({ food, quantity, unit }) => ({ food_id: food._id, quantity, unit }));
    if (!foods.length || foods.length > MAX_MEAL_FOOD_BATCH) {
      throw foodBatchError('rejected', `Choose between 1 and ${MAX_MEAL_FOOD_BATCH} foods.`);
    }
    const payloadKey = JSON.stringify(foods);
    const previousCommitted = count => foodBatchError('previous-committed',
      `Your earlier ${count} ${count === 1 ? 'food was' : 'foods were'} already added. Nothing else was added — check the meal before adding more.`);
    if (restored?.state === 'committed') {
      if (restored.meal) await runMealMutation(async () => restored.meal, { scope });
      if (!isLive()) return { status: 'stale', dispatched: false };
      if (restored.payloadKey === payloadKey) return { status: 'added' };
      throw previousCommitted(restored.foods.length);
    }
    let operation = readFoodBatchOperation(service, identity, scope.mealId);
    if (operation && operation.payloadKey !== payloadKey) {
      if (operation.state !== 'unknown') {
        throw foodBatchError('busy', 'Still adding your previous foods. Try again in a moment.');
      }
      const previous = operation;
      let settled;
      try {
        settled = await service.cancelMealFoodBatch(scope.mealId, previous.operationId);
      } catch (error) {
        if (!isRejectedFoodBatchError(error)) {
          throw foodBatchError('unknown', "Couldn't confirm your earlier add. Retry is safe — foods won't be added twice.");
        }
        settleFoodBatchOperation(service, identity, scope.mealId, previous, 'rejected');
        throw foodBatchError('rejected', `${error.response.data.error} Nothing was added.`);
      }
      settleFoodBatchOperation(service, identity, scope.mealId, previous,
        settled.status === 'committed' ? 'committed' : 'cancelled', settled.meal);
      if (settled.status === 'committed') {
        await runMealMutation(async () => settled.meal, { scope });
        if (!isLive()) return { status: 'stale', dispatched: true };
        throw previousCommitted(previous.foods.length);
      }
      if (!isLive()) return { status: 'stale', dispatched: true };
      operation = null;
    }
    if (!operation) {
      operation = createFoodBatchOperation(service, identity, scope.mealId,
        { operationId: createMealOperationId(), payloadKey, foods, items });
    }
    // A submit while this operation is in flight joins that attempt's outcome.
    const inFlight = operation.promise;
    const claim = claimFoodBatchOperation(operation);
    let dispatched = false;
    const dispatch = () => {
      dispatched = true;
      operation.claims.delete(claim);
      if (inFlight) return inFlight;
      // Queued behind an attempt that has since settled: share its outcome.
      if (operation.state === 'committed') return operation.meal;
      if (operation.state === 'rejected') return Promise.reject(operation.error);
      if (isSettledFoodBatchOperation(operation)) {
        return Promise.reject(foodBatchError('rejected', 'This add was replaced by a newer selection. Nothing was added.'));
      }
      if (!operation.promise) {
        operation.state = 'pending';
        operation.promise = service.addFoodsToMeal(scope.mealId, foods, operation.operationId).then(updatedMeal => {
          operation.promise = null;
          settleFoodBatchOperation(service, identity, scope.mealId, operation, 'committed', updatedMeal);
          return updatedMeal;
        }, error => {
          operation.promise = null;
          if (isRejectedFoodBatchError(error)) {
            operation.error = error;
            settleFoodBatchOperation(service, identity, scope.mealId, operation, 'rejected');
          } else markFoodBatchOperationUnknown(service, identity, scope.mealId, operation);
          throw error;
        });
      }
      return operation.promise;
    };
    try {
      await runMealMutation(dispatch, { scope });
    } catch (error) {
      if (!isLive()) return { status: 'stale', dispatched };
      if (error?.foodBatchOutcome) throw error;
      if (isRejectedFoodBatchError(error)) throw foodBatchError('rejected', `${error.response.data.error} Nothing was added.`);
      throw foodBatchError('unknown', "Couldn't confirm these foods were added. Retry is safe — they won't be added twice.");
    } finally {
      // This submit's queue never sent it (e.g. its Meal unmounted). Another
      // instance may have adopted the same operation, so only drop our claim.
      if (!dispatched) dropFoodBatchClaim(service, identity, scope.mealId, operation, claim);
    }
    return isLive() ? { status: 'added' } : { status: 'stale', dispatched };
  };

  const handleRemoveFood = async (food, legacyIndex = null) => {
    if (!window.confirm('Are you sure you want to remove this food from the meal?')) return;

    try {
      const instanceId = food.instance_id || null;
      const legacyOptions = instanceId ? undefined : {
        legacyIndex,
        expected: {
          food_id: String(food.food_id),
          quantity: food.quantity,
          unit: food.unit ?? null,
          recipe_group_id: food.recipe_group_id || null,
        },
      };
      await runMealMutation(() => instanceId
        ? backendService.removeFoodFromMeal(meal._id, food.food_id, instanceId)
        : backendService.removeFoodFromMeal(meal._id, food.food_id, null, legacyOptions));
    } catch (error) {
      console.error('Error removing food from meal:', error);
      alert('Failed to remove food from meal.');
    }
  };

  const handleUpdate = async (foodId, newQuantity, newUnit, instanceId = null) => {
    if (newQuantity === null || String(newQuantity).trim() === '') return;
    const parsedQuantity = Number(newQuantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 0) return;
    
    // Detect unit change causing quantity conversion
    const food = instanceId 
      ? foods.find(f => f.instance_id === instanceId)
      : foods.find(f => f.food_id === foodId);
    let quantityToSave = parsedQuantity;

    if (food && food.unit !== newUnit && parsedQuantity === Number(food.quantity)) {
        // Unit changed, convert quantity
        // Always prefer legacyFoodMap (full food details) over the snapshot in meal.foods
        const source = legacyFoodMap[foodId] || food;
        
        // Get the food's serving definitions
        const servingWeight = parseFloat(source.serving_weight) || null; // grams per serving
        const servingVolume = parseFloat(source.serving_volume) || null; // ml per serving
        const density = parseFloat(source.density) || 1; // g/ml
        
        // Fallback: if no serving_weight/volume, use serving_size with serving_unit
        let fallbackGrams = null;
        let fallbackMl = null;
        const sSize = parseFloat(source.serving_size) || 100;
        const sUnit = source.serving_quantity_unit || source.serving_unit || 'g';
        if (!servingWeight && !servingVolume) {
            if (['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(sUnit)) {
                fallbackMl = sSize * (CONVERSION_RATES[sUnit] || 1);
                fallbackGrams = fallbackMl * density;
            } else {
                fallbackGrams = sSize * (CONVERSION_RATES[sUnit] || 1);
                fallbackMl = density > 0 ? fallbackGrams / density : fallbackGrams;
            }
        }
        
        const gramsPerServing = servingWeight || fallbackGrams || 100;
        const mlPerServing = servingVolume || fallbackMl || (gramsPerServing / density);
        
        const currentQty = parseFloat(food.quantity) || 0;
        const currentUnit = food.unit;
        
        // Helper to check if unit is volume-based
        const isVolumeUnit = (u) => ['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(u);
        const isMassUnit = (u) => ['g', 'oz', 'lb'].includes(u);
        
        // Step 1: Convert current quantity to "servings" as intermediate
        let servings = 0;
        
        if (currentUnit === 'serving') {
            servings = currentQty;
        } else if (isVolumeUnit(currentUnit)) {
            const mlValue = currentQty * (CONVERSION_RATES[currentUnit] || 1);
            servings = mlPerServing > 0 ? mlValue / mlPerServing : currentQty;
        } else if (isMassUnit(currentUnit)) {
            const gramsValue = currentQty * (CONVERSION_RATES[currentUnit] || 1);
            servings = gramsPerServing > 0 ? gramsValue / gramsPerServing : currentQty;
        } else {
            // Unknown unit, assume it's like grams
            servings = gramsPerServing > 0 ? currentQty / gramsPerServing : currentQty;
        }
        
        // Step 2: Convert servings to the new unit
        if (newUnit === 'serving') {
            quantityToSave = servings;
        } else if (isVolumeUnit(newUnit)) {
            const totalMl = servings * mlPerServing;
            quantityToSave = totalMl / (CONVERSION_RATES[newUnit] || 1);
        } else if (isMassUnit(newUnit)) {
            const totalGrams = servings * gramsPerServing;
            quantityToSave = totalGrams / (CONVERSION_RATES[newUnit] || 1);
        } else {
            // Unknown unit, assume it's like grams
            quantityToSave = servings * gramsPerServing;
        }
        
        quantityToSave = parseFloat(quantityToSave.toFixed(2));
    }

    try {
      return await runMealMutation(() => backendService.updateMealFoodQuantity(
        meal._id, foodId, quantityToSave, newUnit, instanceId
      ));
    } catch (error) {
      console.error('Error updating food:', error);
      throw error;
    }
  };

  // Helper to get "display items" - standalone foods and recipe groups as units.
  // While move mode is active, the source of truth is the shared context
  // state (which can include foods that originated in sibling meals).
  const effectiveFoods = (moveActive && moveFoods.getFoodsFor(mealIdStr)) || foods;
  const getDisplayItems = useCallback(() => {
    const items = [];
    const processedGroups = new Set();
    
    effectiveFoods.forEach((food, idx) => {
      if (food.recipe_group_id) {
        if (!processedGroups.has(food.recipe_group_id)) {
          processedGroups.add(food.recipe_group_id);
          const groupFoods = effectiveFoods.filter(f => f.recipe_group_id === food.recipe_group_id);
          items.push({
            type: 'recipe_group',
            recipe_group_id: food.recipe_group_id,
            recipe_name: food.recipe_name,
            recipe_scale: food.recipe_scale,
            foods: groupFoods,
            startIndex: idx
          });
        }
      } else {
        items.push({
          type: 'food',
          food,
          index: idx
        });
      }
    });
    return items;
  }, [effectiveFoods]);

  // Track the latest foods order in a ref so the drag-end persister can read it
  // without becoming stale due to closures over old `foods` snapshots.
  const foodsRef = useRef(foods);
  useEffect(() => { foodsRef.current = foods; }, [foods]);

  // Drag-drop handler used by DraggableCompactMealItem. Routes through the
  // shared context so cross-meal moves update the correct sibling state.
  const moveItem = useCallback((fromMealId, fromIndex, toMealId, toIndex) => {
    moveFoods.moveItem(fromMealId, fromIndex, toMealId, toIndex);
  }, [moveFoods]);

  // Persist on drag end. Receives the set of meal ids that were touched
  // (origin + destination) so a single backend call updates all of them.
  const persistFoodsOrder = useCallback((touchedMealIds) => {
    const ids = (touchedMealIds && touchedMealIds.length)
      ? touchedMealIds
      : [mealIdStr];
    moveFoods.persistMeals(ids).catch(err => {
      console.error('Error saving food move:', err);
    });
  }, [moveFoods, mealIdStr]);

  const toggleReorderMode = () => {
    if (pendingMutationCount > 0) return;
    // Anchor this meal row through the global layout shift caused by
    // entering/exiting per-day move mode (siblings above can change height).
    // Without this, the viewport can jump to older days when rows reflow.
    const anchorId = `activity-${mealIdStr}`;
    const anchorEl = document.getElementById(anchorId);
    const beforeTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;

    if (moveActive) {
      moveFoods.exit();
      return;
    } else {
      if (moveFoods.enter(mealIdStr) === false) return;
      setCreateRecipeMode(false);
      setSelectedForRecipe(new Set());
    }

    const HEADER_OFFSET = 160;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(anchorId);
        if (!el) return;

        // Step 1: keep the anchor row at the same viewport Y position.
        if (beforeTop !== null) {
          const afterTop = el.getBoundingClientRect().top;
          const delta = afterTop - beforeTop;
          if (Math.abs(delta) > 1) {
            scrollPageBy({ top: delta, left: 0, behavior: 'instant' });
          }
        }

        // Step 2: if still out of a comfortable reading zone, gently correct.
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight;
        if (rect.top < HEADER_OFFSET || rect.top > viewportH * 0.6) {
          const y = rect.top + window.pageYOffset - HEADER_OFFSET;
          scrollPageTo({ top: y, behavior: 'smooth' });
        }
      });
    });
  };

  const toggleCreateRecipeMode = () => {
    if (recipeConversionRef.current.pending || pendingMutationCount > 0 || readOnly) return;
    recipeConversionRef.current.intent = null;
    if (createRecipeMode) {
      moveFoods.exitRecipe(mealIdStr);
      setCreateRecipeMode(false);
      setSelectedForRecipe(new Set());
    } else {
      moveFoods.enterRecipe(mealIdStr);
      setCreateRecipeMode(true);
      if (moveActive) moveFoods.exit();
    }
  };

  const toggleSelection = (key) => {
    if (recipeConversionRef.current.pending) return;
    recipeConversionRef.current.intent = null;
    setSelectedForRecipe(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });
  };

  const handleCreateRecipeFromSelection = async () => {
    const state = recipeConversionRef.current;
    if (readOnly || state.pending || pendingMutationCount > 0) return;
    if (!state.intent) {
      const selection = getDisplayItems().flatMap(item => {
        const key = item.type === 'recipe_group' ? `group-${item.recipe_group_id}` : (item.food.instance_id || item.food._client_id);
        if (!selectedForRecipe.has(key)) return [];
        return (item.type === 'recipe_group' ? item.foods : [item.food]).map(food => food.instance_id ? {
          instance_id: food.instance_id, recipe_group_id: food.recipe_group_id || null,
        } : {
          // Index is only a locator; the server verifies the expected source
          // inside the transaction before assigning a persisted identity.
          legacy_index: effectiveFoods.indexOf(food),
          expected: { food_id: String(food.food_id), quantity: food.quantity,
            unit: food.unit ?? null, recipe_group_id: food.recipe_group_id || null },
        });
      });
      if (!selection.length) return;
      const name = window.prompt("Enter a name for the new recipe:")?.trim();
      if (!name) return;
      state.intent = { name, selection };
    }
    // Ref gates even two clicks in the same render. Keep the exact intent on a
    // lost response so Retry cannot accidentally create a second conversion.
    state.pending = true;
    setIsCreatingRecipe(true);
    const generation = state.generation;
    const isCurrent = () => recipeConversionRef.current === state && state.generation === generation;
    try {
      const updatedMeal = await runMealMutation(async () => {
        const result = await backendService.convertMealSelectionToRecipe(
          meal._id, state.intent.name, state.intent.selection
        );
        return result.meal;
      }, { refresh: false });
      if (!updatedMeal) return;
      if (!isCurrent()) return;
      moveFoods.exitRecipe(mealIdStr);
      setCreateRecipeMode(false);
      setSelectedForRecipe(new Set());
      state.intent = null;
      // The write already succeeded. A parent refresh failure is not a failed
      // conversion and must not offer a fresh mutation as its recovery path.
      try { await onActivityRefresh?.(updatedMeal); }
      catch (error) { console.error('Recipe saved; meal refresh failed', error); }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to create recipe', error);
      alert('Unable to confirm recipe creation. Retry safely, or reload the meal. ' + error.message);
    } finally {
      state.pending = false;
      if (recipeConversionRef.current === state && !state.disposed) setIsCreatingRecipe(false);
    }
  };

  const handleInspectFood = (food) => {
    // Resolve the full details from our map
    const foodDetails = legacyFoodMap[food.food_id] || food;
    modalService.open(FoodSelectionModal, {
        backendService,
        onSelect: handleAddFood,
        initialFood: foodDetails,
        manageMode: true,
        readOnly: true
    });
  };

  const openAddModal = (initialTab = 'food', startWithCamera = false, startWithPhotos = false) => {
    const scope = mutationScopeRef.current;
    const unconfirmedBatch = readFoodBatchOperation(backendService, scope.account.identity, scope.mealId);
    // This picker stays linked to the operation it restores until that link
    // has been reconciled with a settled outcome.
    let restoredBatch = unconfirmedBatch;
    const selectFoods = async items => {
      const restored = restoredBatch;
      try {
        return await handleAddFoods(items, scope, restored);
      } finally {
        if (isSettledFoodBatchOperation(restored)) restoredBatch = null;
      }
    };
    modalService.open(UnifiedAddModal, {
      backendService,
      modalService,
      onSelectFood: handleAddFood,
      onSelectFoods: selectFoods,
      initialSelectedFoods: unconfirmedBatch ? unconfirmedBatch.items : null,
      initialSelectionState: unconfirmedBatch ? (unconfirmedBatch.state === 'unknown' ? 'unknown' : 'pending') : null,
      mealId: meal._id,
      onSelectRecipe: async (recipeId, scale) => {
        try {
          await runMealMutation(() => backendService.addRecipeToMeal(meal._id, recipeId, scale));
        } catch (error) {
          console.error('Error adding recipe to meal:', error);
          if (error.response && error.response.status === 409) {
            alert('Some ingredients from this recipe already exist in your meal.');
          } else {
            alert('Failed to add recipe to meal.');
          }
        }
      },
      initialTab,
      startWithCamera,
      startWithPhotos
    });
  };

  const openCameraFoodModal = () => openAddModal('food', true);
  const openPhotosFoodModal = () => openAddModal('food', false, true);

  const openWaterPicker = () => {
    modalService.open(WaterPickerModal, {
      onSelect: async ({ food_id, quantity, unit }) => {
        try {
          await runMealMutation(() => backendService.addFoodToMeal(meal._id, food_id, quantity, unit));
        } catch (error) {
          console.error('Error logging water:', error);
          alert('Failed to log water.');
        }
      }
    });
  };

  const handleScaleRecipe = async (recipeGroupId, currentScale) => {
    modalService.open(RecipeScaleModal, {
      currentScale,
      hideCloseButton: true,
      onScale: async (newScale) => {
        try {
          await runMealMutation(() => backendService.scaleRecipeInMeal(meal._id, recipeGroupId, newScale));
        } catch (error) {
          console.error('Error scaling recipe:', error);
          throw new Error('Recipe amount could not be updated. Please try again.');
        }
      }
    });
  };

  const handleRemoveRecipe = async (recipeGroupId, recipeName) => {
    if (!window.confirm(`Remove all foods from recipe "${recipeName}"?`)) return;

    try {
      await runMealMutation(() => backendService.removeRecipeFromMeal(meal._id, recipeGroupId));
    } catch (error) {
      console.error('Error removing recipe from meal:', error);
      alert('Failed to remove recipe.');
    }
  };

  const handleSuggestMeal = async (constraints = null, targetCalories = null, images = photoEntries.map(entry => entry.file)) => {
      if (isSuggesting) return;
      const attempt = startSuggestionJob();
      lastSuggestionRequestRef.current = { constraints, targetCalories, images };
      setSuggestion(null);
      setShowConstraintPrompt(false);
      setIsSuggesting(true);
      try {
          const updatedMeal = await backendService.generateMealSuggestionForActivity(meal._id, constraints, targetCalories, images, { signal: attempt.signal });
          if (!isCurrentSuggestionJob(attempt)) return;
          if (updatedMeal && updatedMeal.data && updatedMeal.data.suggestion) {
             setSuggestion(updatedMeal.data.suggestion);
          }
      } catch (e) {
          if (!isCurrentSuggestionJob(attempt)) return;
          setSuggestion({
            error: 'generation_failed',
            code: e?.code || null,
            message: mealSuggestionError(e?.code).message,
          });
          console.error('Meal suggestion generation failed', { code: e?.code || null });
      } finally {
          if (isCurrentSuggestionJob(attempt)) setIsSuggesting(false);
      }
  };

  // Pending -> running is the same subscription, not a new polling attempt.
  const suggestionJobPhase = ['pending', 'running'].includes(meal.meal_suggestion_job?.status)
    ? 'active' : meal.meal_suggestion_job?.status;
  const canonicalSuggestion = suggestionJobPhase === 'succeeded'
    ? JSON.stringify(meal.data.suggestion || null) : null;

  useEffect(() => {
    cancelSuggestionJob();
    setIsSuggesting(false);
    if (meal.meal_suggestion_job?.status === 'cancelled') {
      setSuggestion(null);
      return;
    }
    if (meal.meal_suggestion_job?.status === 'succeeded') {
      setSuggestion(meal.data.suggestion || null);
      return;
    }
    if (meal.meal_suggestion_job?.status === 'failed') {
      const code = meal.meal_suggestion_job.error;
      setSuggestion({ error: 'generation_failed', code, message: mealSuggestionError(code).message });
      return;
    }
    if (!['pending', 'running'].includes(meal.meal_suggestion_job?.status) || readOnly) return;
    const attempt = startSuggestionJob();
    setSuggestion(null); setIsSuggesting(true);
    backendService.resumeMealSuggestionForActivity(meal._id, { signal: attempt.signal }).then(updated => {
      if (isCurrentSuggestionJob(attempt)) setSuggestion(updated?.data?.suggestion || null);
    }).catch(e => {
      if (isCurrentSuggestionJob(attempt)) setSuggestion({ error: 'generation_failed', code: e?.code, message: mealSuggestionError(e?.code).message });
    }).finally(() => { if (isCurrentSuggestionJob(attempt)) setIsSuggesting(false); });
    return () => attempt.abort();
    // Canonical value scope deliberately ignores harmless object-identity feed rerenders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionScope, meal.meal_suggestion_job?.id, suggestionJobPhase, canonicalSuggestion]);

  const handleStartSuggestion = () => {
      setShowConstraintPrompt(true);
  };

  const handleDismissSuggestion = async () => {
      cancelSuggestionJob();
      setIsSuggesting(false);
      setSuggestion(null);
      try {
          await backendService.clearMealSuggestion(meal._id);
      } catch (e) {
          console.error('Error clearing meal suggestion:', e);
      }
  };

  const handleConstraintSubmit = (useSurprise = false) => {
      let finalConstraints = (constraintInput && constraintInput.trim()) ? constraintInput.trim() : null;

      if (!useSurprise && includeExisting && foods.length > 0) {
          // Build a context string from existing foods
          const existingList = foods.map(f => {
               const details = legacyFoodMap[f.food_id] || f;
               const name = details.name || "Unknown Item";
               return `${name} (${f.quantity} ${f.unit})`;
          }).join(", ");
          
          const contextMsg = `\n\n[Context] The meal currently contains: ${existingList}. Please integrate or complement these items in the suggestion.`;
          finalConstraints = (finalConstraints || "Suggest a meal") + contextMsg;
      }

      const parsedTarget = Number(targetCaloriesInput);
      const targetCals = Number.isFinite(parsedTarget) && parsedTarget > 0 ? parsedTarget : null;

      if (useSurprise) {
          handleSuggestMeal(null, targetCals, photoEntries.map(entry => entry.file));
      } else {
          handleSuggestMeal(finalConstraints, targetCals, photoEntries.map(entry => entry.file));
      }
  };

  // Helper to calculate nutrients based on quantity and unit
  const getFoodNutrients = (food) => {
     // Use live data if available (legacyFoodMap), otherwise fallback to snapshot (food)
     const source = legacyFoodMap[food.food_id] || food;
     return calculateFoodNutrients(food, source);
  };

    // Prefer backend-calculated summary so day-level totals and card totals match exactly.
    const totals = meal.nutrition_summary ?? foods.reduce((acc, food) => {
      const n = getFoodNutrients(food);
      return {
        calories: acc.calories + n.calories,
        protein: acc.protein + n.protein,
        carbs: acc.carbs + n.carbs,
        fat: acc.fat + n.fat,
        water_ml: acc.water_ml + (n.water_ml || 0)
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, water_ml: 0 });

  const totalMacroGrams = totals.protein + totals.carbs + totals.fat;
  const proteinPct = totalMacroGrams > 0 ? (totals.protein / totalMacroGrams) * 100 : 33;
  const carbsPct = totalMacroGrams > 0 ? (totals.carbs / totalMacroGrams) * 100 : 33;
  const fatPct = totalMacroGrams > 0 ? (totals.fat / totalMacroGrams) * 100 : 34;
  const waterMl = Math.round(totals.water_ml || 0);

  return (
    <div className="meal-container">
      {createRecipeMode && !moveActive && !readOnly && (
        <FloatingSelectionActions
          label="Recipe selection actions"
          confirmLabel={`Finish (${selectedForRecipe.size})`}
          onConfirm={handleCreateRecipeFromSelection}
          onCancel={toggleCreateRecipeMode}
          disabled={selectedForRecipe.size === 0 || effectiveFoods.length === 0}
          busy={isCreatingRecipe}
        />
      )}
      {!moveVisibility.hideSummary && (
        <div className="meal-summary-card">
        <div className="meal-summary-cals">
          {totals.calories.toFixed(0)}<span className="unit">kcal</span>
        </div>
        {meal?.data?.nutrition_target?.calories && (
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '-4px', marginBottom: '6px' }}>
            target {meal.data.nutrition_target.calories} kcal
            {meal.data.nutrition_target.calories_min && meal.data.nutrition_target.calories_max
              ? ` (${meal.data.nutrition_target.calories_min}–${meal.data.nutrition_target.calories_max})`
              : ''}
          </div>
        )}
        <div className="meal-summary-bar">
          <div className="bar-segment bar-protein" style={{ width: `${proteinPct}%` }} />
          <div className="bar-segment bar-carbs" style={{ width: `${carbsPct}%` }} />
          <div className="bar-segment bar-fat" style={{ width: `${fatPct}%` }} />
        </div>
        <div className="meal-summary-macros">
          <span className="macro protein">{totals.protein.toFixed(0)}<span className="g">g P</span></span>
          <span className="macro carbs">{totals.carbs.toFixed(0)}<span className="g">g C</span></span>
          <span className="macro fat">{totals.fat.toFixed(0)}<span className="g">g F</span></span>
          {waterMl > 0 && (
            <span className="macro water" style={{ color: '#7dd3fc' }} title="Water intake">
              💧{waterMl}<span className="g">ml</span>
            </span>
          )}
        </div>
        </div>
      )}

      {/* Constraint Prompt moved to meal-actions */}

      {/* Loading State and Suggestion Display moved to meal-actions */}
      
      {(effectiveFoods.length > 0 || moveActive) && (
        <div className={`meal-foods-list ${moveActive ? 'reorder-mode-active move-mode-active' : ''}`}>
          {(() => {
            const displayItems = getDisplayItems();
            const dropPreview = moveFoods.dropPreview;
            const previewIndex = dropPreview && dropPreview.mealId === mealIdStr ? dropPreview.index : null;
            const previewHeight = dropPreview?.height || DEFAULT_ROW_HEIGHT;
            const isEditable = !readOnly && !createRecipeMode && (isActivityOngoing(meal) || meal.is_template);
            const showHeader = !readOnly; // Hide reorder/recipe controls in readOnly mode
            
            return (
              <>
                {/* Move and Recipe controls at top */}
                {(displayItems.length >= 1 || moveActive) && showHeader && !moveActive && !createRecipeMode && (
                  <div className="reorder-toggle-row">
                        <>
                            {/* Recipe Toggle */}
                            {!moveActive && (
                                <button className="reorder-toggle-link" onClick={toggleCreateRecipeMode}>
                                    <BeakerIcon style={{ width: 14, height: 14 }} />
                                    Turn into recipe
                                </button>
                            )}

                            {/* Move Foods Toggle. Available even with a single
                                item so the user can drag it to another meal. */}
                            {!moveActive && displayItems.length >= 1 && (
                                <button className="reorder-toggle-link" onClick={toggleReorderMode} disabled={pendingMutationCount > 0}>
                                    <Bars3Icon style={{ width: 14, height: 14 }} />
                                    Move foods
                                </button>
                            )}
                        </>
                  </div>
                )}

                
                {/* Move Mode - Show draggable compact items */}
                {moveActive && (
                  <>
                    <MealCustomDragLayer />
                    {displayItems.map((item, itemIndex) => {
                      if (item.type === 'recipe_group') {
                        return (
                          <React.Fragment key={item.recipe_group_id}>
                            {previewIndex === itemIndex && (
                              <MealDropPlaceholder
                                mealId={mealIdStr}
                                height={previewHeight}
                                moveItem={moveItem}
                              />
                            )}
                            <MealInsertDropZone
                              mealId={mealIdStr}
                              index={itemIndex}
                              moveItem={moveItem}
                            />
                            <DraggableCompactMealItem
                              item={item}
                              index={itemIndex}
                              mealId={mealIdStr}
                              moveItem={moveItem}
                              onDragEnd={persistFoodsOrder}
                              isRecipe={true}
                              name={item.recipe_name || 'Recipe'}
                              detail={`${item.foods.length} items`}
                            />
                          </React.Fragment>
                        );
                      } else {
                        const food = item.food;
                        const source = legacyFoodMap[food.food_id] || food;
                        const displayName = legacyFoodMap[food.food_id]?.name || food.name || 'Unknown Food';
                        let displayUnit = food.unit || source.std_unit || source.serving_unit || 'g';
                        if (!UNIT_OPTIONS.includes(displayUnit)) displayUnit = 'serving';
                        
                        return (
                          <React.Fragment key={food.instance_id || food._client_id || `${food.food_id}-${item.index}`}>
                            {previewIndex === itemIndex && (
                              <MealDropPlaceholder
                                mealId={mealIdStr}
                                height={previewHeight}
                                moveItem={moveItem}
                              />
                            )}
                            <MealInsertDropZone
                              mealId={mealIdStr}
                              index={itemIndex}
                              moveItem={moveItem}
                            />
                            <DraggableCompactMealItem
                              item={item}
                              index={itemIndex}
                              mealId={mealIdStr}
                              moveItem={moveItem}
                              onDragEnd={persistFoodsOrder}
                              isRecipe={false}
                              name={displayName}
                              detail={`${parseFloat(Number(food.quantity).toFixed(2))} ${displayUnit}`}
                            />
                          </React.Fragment>
                        );
                      }
                    })}
                    {previewIndex === displayItems.length && (
                      <MealDropPlaceholder
                        mealId={mealIdStr}
                        height={previewHeight}
                        moveItem={moveItem}
                      />
                    )}
                    {/* Tail drop zone — lets users drop foods at the end of
                        the list, including into otherwise-empty meals. */}
                    <MealTailDropZone
                      mealId={mealIdStr}
                      itemCount={displayItems.length}
                      moveItem={moveItem}
                    />
                  </>
                )}

                {moveActive && moveFoods.activeOwnerMealId === mealIdStr && (
                  <FloatingSelectionActions label="Move foods actions" onConfirm={toggleReorderMode} />
                )}
                
                {/* Normal Mode - Show full food cards */}
                {!moveActive && displayItems.map((item, itemIndex) => {
                  const selectionKey = item.type === 'recipe_group' ? `group-${item.recipe_group_id}` : (item.food.instance_id || item.food._client_id);
                  const isSelected = selectedForRecipe.has(selectionKey);
                  const containerClass = createRecipeMode 
                      ? (isSelected ? ' selection-mode selected' : ' selection-mode') 
                      : '';
                  
                  const handleSelectionClick = createRecipeMode ? (e) => {
                      e.preventDefault(); 
                      e.stopPropagation();
                      toggleSelection(selectionKey);
                  } : undefined;
                  
                  const pointerEventsStyle = createRecipeMode ? { pointerEvents: 'none' } : {};

                  if (item.type === 'recipe_group') {
                    // Render recipe group
                    return (
                      <div 
                        key={item.recipe_group_id} 
                        className={`recipe-group${containerClass}`}
                        onClick={handleSelectionClick}
                      >
                        <div className="recipe-group-header" style={pointerEventsStyle}>
                          <div className="recipe-group-info">
                            <BeakerIcon className="icon-sm" />
                            <span className="recipe-group-name">{item.recipe_name || 'Recipe'}</span>
                            <span className="recipe-group-scale">({((item.recipe_scale || 1) * 100).toFixed(0)}%)</span>
                          </div>
                          {isEditable && (
                            <div className="recipe-group-actions">
                              <button 
                                className="btn-scale-recipe"
                                onClick={() => handleScaleRecipe(item.recipe_group_id, item.recipe_scale || 1)}
                                title="Scale recipe"
                              >
                                <ScaleIcon className="icon-sm" />
                              </button>
                              <button 
                                className="btn-remove-recipe"
                                onClick={() => handleRemoveRecipe(item.recipe_group_id, item.recipe_name)}
                                title="Remove recipe"
                              >
                                <TrashIcon className="icon-sm" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="recipe-group-foods" style={pointerEventsStyle}>
                          {item.foods.map((food, foodIdx) => {
                            const nutrients = getFoodNutrients(food);
                            const source = legacyFoodMap[food.food_id] || food;
                            const displayName = legacyFoodMap[food.food_id]?.name || food.name || 'Unknown Food';
                            const displayBrand = legacyFoodMap[food.food_id]?.brand || food.brand || '';
                            let displayUnit = food.unit || source.std_unit || source.serving_unit || 'g';
                            if (!UNIT_OPTIONS.includes(displayUnit)) displayUnit = 'serving';
                            
                            return (
                              <div key={food.instance_id || `${food.food_id}-${foodIdx}`} className="meal-food-card recipe-item">
                                <div className="meal-food-info">
                                  <div 
                                    className="meal-food-name" 
                                    onClick={() => handleInspectFood(food)} 
                                    style={{cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#555'}}
                                    title="View details / Edit food"
                                  >
                                    {displayName}
                                  </div>
                                  <div className="meal-food-brand">{displayBrand}</div>
                                  <div className="meal-food-macros">
                                    <span>{nutrients.calories.toFixed(0)} kcal</span> • 
                                    <span>P: {nutrients.protein.toFixed(1)}</span> • 
                                    <span>C: {nutrients.carbs.toFixed(1)}</span> • 
                                    <span>F: {nutrients.fat.toFixed(1)}</span>
                                  </div>
                                </div>
                                <div className="meal-food-actions">
                                  <div className="meal-quantity-wrapper">
                                    {!isEditable ? (
                                      <span className="meal-quantity-display">{parseFloat(Number(food.quantity).toFixed(2))} {displayUnit}</span>
                                    ) : (
                                      <>
                                        <div className="meal-qty-unit-row">
                                          <MealQuantityInput
                                            aria-label={`Quantity for ${displayName}`}
                                            min="0" step="0.01"
                                            value={food.quantity}
                                            onCommit={(val) => handleUpdate(food.food_id, val, displayUnit, food.instance_id)}
                                            scopeKey={mealIdStr}
                                            className="meal-qty-input"
                                          />
                                          <select 
                                            aria-label={`Unit for ${displayName}`}
                                            value={displayUnit}  
                                            onChange={(e) => handleUpdate(food.food_id, food.quantity, e.target.value, food.instance_id)}
                                            className="meal-unit-select"
                                          >
                                            {!UNIT_OPTIONS.includes(displayUnit) && <option value={displayUnit}>{displayUnit}</option>}
                                            {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                                          </select>
                                        </div>
                                      </>
                                    )}
                                    {source.serving_description && (() => {
                                      const servings = calculateServings(food.quantity, displayUnit, source);
                                      if (servings && servings > 0) {
                                        if (servings === 1) {
                                          return <div className="meal-serving-hint">{source.serving_description}</div>;
                                        }
                                        const formatted = servings % 1 === 0 ? servings : servings.toFixed(2);
                                        return <div className="meal-serving-hint">{formatted} × {source.serving_description}</div>;
                                      }
                                      return null;
                                    })()}
                                  </div>
                                  {isEditable && (
                                    <button
                                      className="btn-remove-food"
                                      onClick={() => handleRemoveFood(food, effectiveFoods.indexOf(food))}
                                      title={`Remove ${displayName}`}
                                      aria-label={`Remove ${displayName}`}
                                    >
                                      <TrashIcon className="icon-sm" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } else {
                    // Render standalone food
                    const food = item.food;
                    const nutrients = getFoodNutrients(food);
                    const source = legacyFoodMap[food.food_id] || food;
                    const displayName = legacyFoodMap[food.food_id]?.name || food.name || 'Unknown Food';
                    const displayBrand = legacyFoodMap[food.food_id]?.brand || food.brand || '';
                    let displayUnit = food.unit || source.std_unit || source.serving_unit || 'g';
                    if (!UNIT_OPTIONS.includes(displayUnit)) displayUnit = 'serving';
                    
                    return (
                      <div 
                        key={food.instance_id || `${food.food_id}-${item.index}`} 
                        className={`meal-food-card${containerClass}`}
                        onClick={handleSelectionClick}
                      >
                        <div className="meal-food-info" style={pointerEventsStyle}>
                          <div 
                            className="meal-food-name" 
                            onClick={() => handleInspectFood(food)} 
                            style={{cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#555'}}
                            title="View details / Edit food"
                          >
                            {displayName}
                          </div>
                          <div className="meal-food-brand">{displayBrand}</div>
                          <div className="meal-food-macros">
                            <span>{nutrients.calories.toFixed(0)} kcal</span> • 
                            <span>P: {nutrients.protein.toFixed(1)}</span> • 
                            <span>C: {nutrients.carbs.toFixed(1)}</span> • 
                            <span>F: {nutrients.fat.toFixed(1)}</span>
                          </div>
                        </div>
                        <div className="meal-food-actions" style={pointerEventsStyle}>
                          <div className="meal-quantity-wrapper">
                            {!isEditable ? (
                              <span className="meal-quantity-display">{parseFloat(Number(food.quantity).toFixed(2))} {displayUnit}</span>
                            ) : (
                              <>
                                <div className="meal-qty-unit-row">
                                  <MealQuantityInput
                                    aria-label={`Quantity for ${displayName}`}
                                    min="0" step="0.01"
                                    value={food.quantity}
                                    onCommit={(val) => handleUpdate(food.food_id, val, displayUnit, food.instance_id)}
                                    scopeKey={mealIdStr}
                                    className="meal-qty-input"
                                  />
                                  <select
                                    aria-label={`Unit for ${displayName}`}
                                    value={displayUnit}
                                    onChange={(e) => handleUpdate(food.food_id, food.quantity, e.target.value, food.instance_id)}
                                    className="meal-unit-select"
                                  >
                                    {!UNIT_OPTIONS.includes(displayUnit) && <option value={displayUnit}>{displayUnit}</option>}
                                    {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </div>
                              </>
                            )}
                            {source.serving_description && (() => {
                              const servings = calculateServings(food.quantity, displayUnit, source);
                              if (servings && servings > 0) {
                                if (servings === 1) {
                                  return <div className="meal-serving-hint">{source.serving_description}</div>;
                                }
                                const formatted = servings % 1 === 0 ? servings : servings.toFixed(2);
                                return <div className="meal-serving-hint">{formatted} × {source.serving_description}</div>;
                              }
                              return null;
                            })()}
                          </div>
                          {isEditable && (
                            <button
                              className="btn-remove-food"
                              onClick={() => handleRemoveFood(food, item.index)}
                            >
                              <TrashIcon className="icon-sm" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }
                })}
              </>
            );
          })()}
        </div>
      )}

      {!moveVisibility.hideActions && (
        <div className="meal-actions">
          {!readOnly && (isActivityOngoing(meal) || meal.is_template) && (
            <div className="meal-action-buttons">
              <div className="meal-action-row meal-food-action-row">
                <button className="btn-add-food meal-action-primary" onClick={() => openAddModal('food')}>
                  <FoodStackPlusIcon className="icon-sm" /> Add Existing
                </button>
                <button className="btn-add-food btn-photo icon-only" onClick={openCameraFoodModal} title="Add from camera" aria-label="Add from camera">
                    <CameraPlusIcon className="icon-sm" />
                </button>
                <button className="btn-add-food btn-photo icon-only" onClick={openPhotosFoodModal} title="Add from photos" aria-label="Add from photos">
                    <PhotoPlusIcon className="icon-sm" />
                </button>
                <button
                    className="btn-add-food btn-photo icon-only"
                    onClick={openWaterPicker}
                    title="Add water"
                    aria-label="Add water"
                >
                  <WaterGlassPlusIcon className="icon-sm" />
                </button>
              </div>
              
              <div className="meal-action-row meal-suggestion-row">
                {!showConstraintPrompt && !isSuggesting && !suggestion && (
                  <button
                      className="btn-add-food btn-suggest"
                      onClick={handleStartSuggestion} 
                      title="Get AI Meal Recommendation"
                  >
                      <LightBulbIcon className="icon-sm" /> Suggest
                  </button>
                )}

                {showConstraintPrompt && (
                  <MealSuggestionRequestForm
                    constraintInput={constraintInput}
                    setConstraintInput={setConstraintInput}
                    targetCaloriesInput={targetCaloriesInput}
                    setTargetCaloriesInput={setTargetCaloriesInput}
                    includeExisting={includeExisting}
                    setIncludeExisting={setIncludeExisting}
                    hasExistingFoods={foods.length > 0}
                    defaultTargetLabel={meal?.data?.nutrition_target?.calories
                      ? `Default: ${meal.data.nutrition_target.calories} kcal${meal.data.nutrition_target.calories_min && meal.data.nutrition_target.calories_max ? ` (${meal.data.nutrition_target.calories_min}–${meal.data.nutrition_target.calories_max})` : ''}`
                      : 'e.g., 600'}
                    photoEntries={photoEntries}
                    photoFeedback={photoFeedback}
                    onFilesSelected={addPhotos}
                    onRemovePhoto={removePhoto}
                    onGenerate={() => handleConstraintSubmit(false)}
                    onSurprise={() => handleConstraintSubmit(true)}
                    onCancel={() => setShowConstraintPrompt(false)}
                  />
                )}

                {isSuggesting && (
                  <GenerationCue
                    icon="🍳"
                    message="PREPARING YOUR RECIPE — THIS MAY TAKE A FEW MINUTES..."
                  />
                )}

                {isSuggesting && <button type="button" onClick={handleDismissSuggestion}>Cancel recipe</button>}
                {suggestion?.error && (
                  <MealSuggestionFailure
                    onRetry={() => handleSuggestMeal(
                      lastSuggestionRequestRef.current.constraints,
                      lastSuggestionRequestRef.current.targetCalories,
                      lastSuggestionRequestRef.current.images
                    )}
                    onEdit={() => { setSuggestion(null); setShowConstraintPrompt(true); }}
                    message={suggestion.message}
                    retryable={suggestion.code !== 'NO_COMPATIBLE_IMAGE_MODEL'}
                    onDismiss={handleDismissSuggestion}
                  />
                )}

                {suggestion && !suggestion.error && (
                    <div className="meal-suggestion-container" style={{
                        border: '1px solid rgba(129, 140, 248, 0.4)',
                        borderRadius: '16px',
                        padding: '16px',
                        fontSize: '0.9rem',
                        color: '#e5e7eb',
                        width: '100%',
                        boxSizing: 'border-box',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 15px rgba(124, 58, 237, 0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ fontSize: '1.2rem' }}>✨</div>
                                <strong style={{ 
                                    background: 'linear-gradient(90deg, #fff, #a78bfa)', 
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: '1.1rem',
                                    fontWeight: '800'
                                }}>{suggestion.name}</strong>
                            </div>
                            <button 
                              onClick={handleDismissSuggestion}
                              style={{ 
                                background: 'rgba(255,255,255,0.05)', 
                                border: 'none', 
                                color: '#9ca3af', 
                                cursor: 'pointer', 
                                borderRadius: '50%', 
                                width: '28px', 
                                height: '28px', 
                                minWidth: '28px',
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                padding: 0
                              }}
                              title="Dismiss suggestion"
                            >
                              <XMarkIcon style={{ width: 16, height: 16 }} />
                            </button>
                        </div>

                        {/* Structured suggestion display */}
                        {suggestion.ingredients && suggestion.totals && suggestion.instructions ? (
                          <div style={{ lineHeight: '1.5', marginBottom: '16px', width: '100%' }}>
                            {/* Ingredients */}
                            <div style={{ marginBottom: '16px', width: '100%' }}>
                              <div style={{ color: '#818cf8', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <BeakerIcon style={{ width: 14, height: 14 }} /> Ingredients
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                {suggestion.ingredients.map((ing, idx) => (
                                  <div key={idx} className="ingredient-item-anim" style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '8px 12px', 
                                    background: 'rgba(255,255,255,0.03)', 
                                    border: '1px solid rgba(255,255,255,0.05)', 
                                    borderRadius: '8px',
                                    animation: `stagger-in 0.4s ease-out both ${idx * 0.05}s`
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                                      <button
                                        onClick={() => handleAddFoodFromSuggestion(ing, idx)}
                                        disabled={isAddingIngredient !== null}
                                        style={{
                                          background: 'rgba(129, 140, 248, 0.2)',
                                          border: '1px solid rgba(129, 140, 248, 0.3)',
                                          color: '#fff',
                                          borderRadius: '6px',
                                          width: '28px',
                                          height: '28px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: 0,
                                          cursor: 'pointer',
                                          fontSize: '1.1rem',
                                          transition: 'all 0.2s ease'
                                        }}
                                        title="Add this food to meal"
                                      >
                                        {isAddingIngredient === idx ? '...' : <PlusIcon style={{ width: 16, height: 16 }} />}
                                      </button>
                                      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <span style={{ color: '#f3f4f6', fontWeight: '500' }}>{ing.name}</span>
                                        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{ing.quantity}</span>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: '12px', flexShrink: 0 }}>
                                      <span style={{ color: '#818cf8', fontWeight: '700', fontSize: '0.85rem' }}>{ing.calories}</span>
                                      <span style={{ color: '#6b7280', fontSize: '0.65rem', textTransform: 'uppercase' }}>kcal</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Macro Totals Grid */}
                            <div className="macros-grid-anim" style={{
                              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px',
                              background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '12px', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.05)'
                            }}>
                              <div>
                                <div style={{ color: '#9ca3af', fontSize: '0.6rem', textTransform: 'uppercase', marginBottom: '2px', fontWeight: '700' }}>Cals</div>
                                <div style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{suggestion.totals.calories}</div>
                              </div>
                              <div>
                                <div style={{ color: '#8b5cf6', fontSize: '0.6rem', textTransform: 'uppercase', marginBottom: '2px', fontWeight: '700' }}>Prot</div>
                                <div style={{ color: '#a78bfa', fontWeight: 800, fontSize: '1rem' }}>{suggestion.totals.protein}<span style={{fontSize: '0.7rem'}}>g</span></div>
                              </div>
                              <div>
                                <div style={{ color: '#f59e0b', fontSize: '0.6rem', textTransform: 'uppercase', marginBottom: '2px', fontWeight: '700' }}>Carb</div>
                                <div style={{ color: '#fbbf24', fontWeight: 800, fontSize: '1rem' }}>{suggestion.totals.carbs}<span style={{fontSize: '0.7rem'}}>g</span></div>
                              </div>
                              <div>
                                <div style={{ color: '#ef4444', fontSize: '0.6rem', textTransform: 'uppercase', marginBottom: '2px', fontWeight: '700' }}>Fat</div>
                                <div style={{ color: '#f87171', fontWeight: 800, fontSize: '1rem' }}>{suggestion.totals.fat}<span style={{fontSize: '0.7rem'}}>g</span></div>
                              </div>
                            </div>

                            {/* Instructions */}
                            <div className="instruction-step-anim" style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '12px', padding: '12px', border: '1px solid rgba(255,255,255,0.03)' }}>
                              <div style={{ color: '#9ca3af', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
                                👨‍🍳 Preparation
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {suggestion.instructions.map((step, idx) => (
                                  <div key={idx} className="instruction-step-anim" style={{ display: 'flex', gap: '10px', animationDelay: `${0.3 + (idx * 0.1)}s` }}>
                                    <span style={{ color: '#4f46e5', fontWeight: '900', fontSize: '0.9rem' }}>{idx + 1}</span>
                                    <span style={{ color: '#d1d5db', fontSize: '0.85rem', lineHeight: '1.5', minWidth: 0, flex: 1 }}>
                                      {step.replace(/^(Step\s*\d+[:.]\s*)/i, '')}
                                      {!readOnly && meal.user_id && extractTimedWait(step) !== null &&
                                        <RecipeStepTimer userId={meal.user_id} mealId={meal._id} instructions={suggestion.instructions} index={idx} />}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Fallback for old-format suggestions */
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4', marginBottom: '16px', color: '#d1d5db' }}>
                            {suggestion.details}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleAddAllFromSuggestion}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    animation: 'meal-float 3s ease-in-out infinite'
                                }}
                            >
                                <PlusIcon style={{ width: 18, height: 18 }} />
                                Add Everything
                            </button>
                            <button
                                onClick={handleStartSuggestion}
                                style={{
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    background: 'rgba(255,255,255,0.05)',
                                    color: '#e5e7eb',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <LightBulbIcon style={{width: 20, height: 20}} />
                            </button>
                        </div>
                    </div>
                )}
              </div>
            </div>
          )}
          
          {!readOnly && (isActivityOngoing(meal) || completion.pending) && !meal.is_template && (
              <SubmitFeedbackButton
                className="btn-submit-meal"
                label="Submit Meal"
                icon={<CheckIcon />}
                feedback={completion}
                noun={SUBMIT_NOUNS.meal}
                onClick={() => finishActivity(meal._id)}
              />
          )}
          <SubmitFeedbackAnnouncement feedback={completion} noun={SUBMIT_NOUNS.meal} />
        </div>
      )}
    </div>
  );
};

export default Meal;
